import React, { Component } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { withRouter } from "react-router-dom";
import debounce from "lodash/debounce";
import { getSections, getSpecialFeatures } from "actions/navigation";
import { logOut, toggleAuthDrawer, changeStep } from "actions/authDrawer";
import { freezeScroll, thawScroll } from "utils/toggleScroll";

import NavigationWrapper from "../NavigationWrapper";
import NoStyleButton from "components/Library/NoStyleButton";
import LogoLink from "../LogoLink";
import Icon from "components/Library/Icon";

import styles from "./NewNavigation.css";
import NewNavigationDropdown from "./NewNavigationDropdown";

export class NewNavigation extends Component {
  constructor(props) {
    super(props);

    this.toggle = this._toggle.bind(this);
    this.toggleLatest = this._toggleLatest.bind(this);
    this.openAuthDrawer = this._openAuthDrawer.bind(this);
    this.scrollHandler = debounce(this.handleScroll.bind(this), 20);

    this.state = {
      isOpen: false,
      isLatestOpen: false,
      navigationScrolled: false,
    };
    this.menuButtonElement = React.createRef();
    this.latestStoriesButtonElement = React.createRef();
  }

  _toggle() {
    this.setState({
      isOpen: !this.state.isOpen,
      isLatestOpen: false,
    });
  }

  _toggleLatest() {
    this.setState({
      isLatestOpen: !this.state.isLatestOpen,
      isOpen: false,
    });
  }

  _openAuthDrawer(step) {
    this.setState({
      isLatestOpen: false,
      isOpen: false,
    });
    this.props.toggleAuthDrawer(true);
    this.props.changeStep(step);
  }

  handleScroll() {
    if (window.scrollY > 110 && !this.state.navigationScrolled) {
      this.setState({
        navigationScrolled: true,
      });
    } else if (window.scrollY < 110 && this.state.navigationScrolled) {
      this.setState({
        navigationScrolled: false,
      });
    }
  }

  componentDidMount() {
    if (!this.props.navigation.results.sections.length) {
      this.props.getSections();
    }
    if (!this.props.navigation.results.specialFeatures.length) {
      this.props.getSpecialFeatures();
    }
    window.addEventListener("scroll", this.scrollHandler);
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.scrollHandler);
  }

  componentDidUpdate(prevProps, prevState) {
    // If URL changes, close the dropdown menu
    if (this.props.location !== prevProps.location) {
      this.setState({
        isOpen: false,
        isLatestOpen: false,
      });
    }
    //If menu dropdown gets closed, shift focus back to menuButton
    if (!this.state.isOpen && prevState.isOpen && this.menuButtonElement) {
      this.menuButtonElement.current.focus();
    }
    //If latestStories dropdown gets closed, shift focus back to latestStoriesButton
    if (
      !this.state.isLatestOpen &&
      prevState.isLatestOpen &&
      this.latestStoriesButtonElement
    ) {
      this.latestStoriesButtonElement.current.focus();
    }
  }

  render() {
    const { isLatestOpen, isOpen } = this.state;
    const dropdownIsOpen = isLatestOpen || isOpen;
    if (dropdownIsOpen) {
      freezeScroll();
    } else {
      thawScroll();
    }
    const wrapperClass = dropdownIsOpen ? styles.open : "";
    const conditionalFocus = dropdownIsOpen ? "-1" : "0";
    return (
      <NavigationWrapper className={wrapperClass}>
        <NoStyleButton
          buttonRef={this.menuButtonElement}
          tabIndex={conditionalFocus}
          onClick={this.toggle}
          trackingClassName={classNames(
            styles.menuButton,
            "gtm-hamburger-menu"
          )}
          className={classNames(styles.toggler, {
            [styles.togglerOpen]: this.state.isOpen,
          })}
          aria-haspopup="true"
          aria-expanded={this.state.isOpen}
          ariaLabel="Open main menu"
        >
          <Icon name="hamburger" className={styles.icon} />
        </NoStyleButton>

        <LogoLink tabIndex={conditionalFocus} />

        <NoStyleButton
          buttonRef={this.latestStoriesButtonElement}
          tabIndex={conditionalFocus}
          onClick={this.toggleLatest}
          trackingClassName={classNames(
            styles.latestStoriesButton,
            "gtm-timer-icon"
          )}
          className={classNames(styles.toggler, {
            [styles.togglerOpen]: this.state.isLatestOpen,
          })}
          aria-haspopup="true"
          aria-expanded={this.state.isLatestOpen}
          ariaLabel="Open latest stories"
        >
          <Icon name="timer" className={styles.timerIcon} />
        </NoStyleButton>

        <NewNavigationDropdown
          isOpen={this.state.isOpen}
          isLatestOpen={this.state.isLatestOpen}
          toggle={this.toggle}
          results={this.props.navigation.results}
          isLoggedIn={this.props.isLoggedIn}
          authDrawerActions={this.props.logOut}
          openAuthDrawer={this.openAuthDrawer}
        />
      </NavigationWrapper>
    );
  }
}

NewNavigation.propTypes = {
  location: PropTypes.object,
  isLoggedIn: PropTypes.bool,
  navigation: PropTypes.shape({
    results: PropTypes.shape({
      sections: PropTypes.array,
      specialFeatures: PropTypes.array,
    }),
  }),
  getSpecialFeatures: PropTypes.func,
  getSections: PropTypes.func,
  toggleAuthDrawer: PropTypes.func,
  changeStep: PropTypes.func,
};

const mapStateToProps = state => {
  return {
    navigation: state.navigation,
    isLoggedIn: !!state.authDrawer.authToken,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    getSections: bindActionCreators(getSections, dispatch),
    getSpecialFeatures: bindActionCreators(getSpecialFeatures, dispatch),
    logOut: bindActionCreators(logOut, dispatch),
    toggleAuthDrawer: bindActionCreators(toggleAuthDrawer, dispatch),
    changeStep: bindActionCreators(changeStep, dispatch),
  };
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(NewNavigation)
);
